import { ChartDisplay, DynamicChartData, HHData, HHDataVector, MetricKey } from "../types";
import * as Enums from "./Enums";

export function transformVectorData(data: HHDataVector[]): HHData[] {
    const transformedData: HHData[] = [];
    
    data.forEach((item: HHDataVector) => {
        const readDate = new Date(item.read_date);
        for (let i = 0; i < 48; i++) {
            const minutesToAdd = i * 30;
            const periodDate = new Date(readDate);
            periodDate.setMinutes(readDate.getMinutes() + minutesToAdd);
            const hhKey = `HH${String(i + 1).padStart(2, '0')}`;

            const transformedDataPoint: HHData =  {
                period_end: periodDate,
                kwh: item[hhKey] as number,
                site_group: item.site_group,
                account_name: item.account_name,
                supply_type: item.supply_type,
                account_id: item.account_id,
                granularity: Enums.PeriodEnum.HOURLY,
                supply_number: item.supply_number,
                site_address: item.site_address,
                site_id: item.site_id,
                supply_id: item.supply_id,
                supply_subtype: item.supply_subtype,
                eb_intensitymetric1value: item.eb_intensitymetric1value,
                eb_intensitymetric2value: item.eb_intensitymetric2value,
                eb_intensitymetric3value: item.eb_intensitymetric3value,
                is_export: item.is_export,
                is_submeter: item.is_submeter,
            };

            transformedData.push(transformedDataPoint);
        }
    });

    return transformedData;
}

export function formatForRecharts(data: HHData[], chartDisplay: ChartDisplay, metric: MetricKey): { chartData: DynamicChartData[], uniqueGroups: string[] } {
    // Map of "timestamp" -> Map<groupName, consumption>
    const groupedData = new Map<string, Map<string, number>>();

    // Keep track of all unique groups (for chart series)
    const groupsSet = new Set<string>();

    // Keep track of import groups only (for average calculation)
    const importGroupsSet = new Set<string>();

    // Accumulate total import consumption for overall average
    let totalOverallConsumption = 0;

    data.forEach((dataPoint) => {
        if(chartDisplay?.supplies.includes(dataPoint.supply_number)) {
            let group: string | undefined;
            switch (chartDisplay?.grouping) {
                case Enums.GroupEnum.ACCOUNT:
                    group = dataPoint.account_name;
                    break;
                case Enums.GroupEnum.SITE_GROUP:
                    group = dataPoint.site_group;
                    break;
                case Enums.GroupEnum.SUPPLY_TYPE:
                    group = dataPoint.supply_type;
                    break;
                case Enums.GroupEnum.SUPPLY_NUMBER:
                    group = dataPoint.supply_number;
                    break;
                case Enums.GroupEnum.SITE:
                    group = dataPoint.site_address;
                    break;
                case Enums.GroupEnum.TOTAL:
                    group = "Total";
                    break;
                default:
                    break;
            }

            // Label export meters, if relevant
            if (dataPoint.is_export) group = `Export - ${group}`;

            if (dataPoint.is_submeter) group = `Submeter - ${group}`;
            
            // Only process if we have a group and a valid kWh
            if (group && (dataPoint.kwh || dataPoint.kwh === 0)) {
                let value = getMetricValue(dataPoint, metric);

                if (dataPoint.is_export) {
                    // Make exports negative (for display, if needed)
                    value *= -1;
                } else if (!dataPoint.is_submeter) {
                    // Only add to the overall total if it's *import*
                    totalOverallConsumption += value;
                    importGroupsSet.add(group);
                }

                const period = new Date(dataPoint.period_end).toISOString();

                if (!groupedData.has(period)) {
                    groupedData.set(period, new Map<string, number>());
                }

                const groupMap = groupedData.get(period)!;
                groupMap.set(group, (groupMap.get(group) || 0) + value);

                groupsSet.add(group);
            }
        }
    });

    // Calculate overall average as total import / # of timestamps / # of import groups
    // (If no import groups exist, handle division by zero accordingly)
    const timestampsCount = groupedData.size;
    const importGroupCount = importGroupsSet.size;
    
    const overallAverage = 
        importGroupCount && timestampsCount 
            ? totalOverallConsumption / timestampsCount / importGroupCount 
            : 0;

    // Build the chart data array
    const chartData: DynamicChartData[] = [];

    groupedData.forEach((groupMap, period) => {
        const dataPoint: DynamicChartData = { date: new Date(period) };

        // Initialize all groups with 0 consumption
        groupsSet.forEach((group) => {
            dataPoint[group] = 0;
        });

        // Populate with actual consumption data
        groupMap.forEach((consumption, group) => {
            dataPoint[group] = consumption;
        });

        // Calculate the average consumption for just the import groups in this time period
        let importSum = 0;
        let countImportGroups = 0;
        groupMap.forEach((val, grp) => {
            // Skip anything that starts with "Export - " or "Submeter - "
            if (!grp.startsWith("Export - ") && !grp.startsWith("Submeter - ")) {
                importSum += val;
                countImportGroups++;
            }
        });

        // This per‐period average (trend) should exclude export
        dataPoint.trend = countImportGroups
            ? importSum / countImportGroups 
            : 0;

        // Everyone gets the same overall average (again, import only)
        dataPoint.overallAverage = overallAverage;

        chartData.push(dataPoint);
    });

    // Sort by timestamp ascending
    chartData.sort((a, b) => a.date.getTime() - b.date.getTime());

    // Return data and list of all unique groups
    return {
        chartData,
        uniqueGroups: Array.from(groupsSet),
    };
}

function getMetricValue(
    dataPoint: HHData,
    metric: MetricKey            // "kWh" | "metric1" | "metric2" | "metric3"
  ): number {
    const kwh = dataPoint.kwh ?? 0;
    let denom: number | null;
  
    switch (metric) {
      case "kWh":
        return kwh;   
  
      case "metric1":
        denom = dataPoint.eb_intensitymetric1value;
        break;
  
      case "metric2":
        denom = dataPoint.eb_intensitymetric2value;
        break;
  
      case "metric3":
        denom = dataPoint.eb_intensitymetric3value;
        break;
  
      default:
        return kwh;
    }
  
    // guard against missing or zero denominator
    if (!denom || denom === 0) return 0;
    return kwh / denom;
  }
